
// Apple resolves the visitor's own storefront and drops the query doing it,
// so there is no language to ask for here — unlike Play below.
const APP_STORE_URL = 'https://apps.apple.com/app/id6766953909';
const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=de.hypofriend.search';

// Only long enough to stop the store bouncing us straight back into itself.
// Ten minutes meant a second, deliberate tap did nothing at all.
const STORE_HANDOFF_GUARD_MS = 5000;

export default {
  name: 'PropertyRadar',

  layout: 'default',

  data() {
    return {
      redirecting: false,
      redirectingToStore: false,
      handoffTimeoutId: null,
      storePaintCleanup: null,
      messageIndex: 0,
      loaderFadeClass: 'is-in',
      rotateIntervalId: null,
      fadeTimeoutId: null,
    };
  },

  head() {
    return {
      title: this.$i18n.t('seo.title'),
      meta: [
        { hid: 'robots', name: 'robots', content: 'noindex, nofollow' },
        {
          hid: 'description',
          name: 'description',
          content: this.$i18n.t('seo.description'),
        },
        { hid: 'ogType', property: 'og:type', content: 'website' },
        {
          hid: 'ogTitle',
          property: 'og:title',
          content: this.$i18n.t('seo.title'),
        },
        {
          hid: 'ogDescription',
          property: 'og:description',
          content: this.$i18n.t('seo.description'),
        },
      ],
    };
  },

  computed: {
    token() {
      const { token } = this.$route.query;
      return (Array.isArray(token) ? token[0] : token) || '';
    },
    exposeId() {
      const { exposeId } = this.$route.query;
      return (Array.isArray(exposeId) ? exposeId[0] : exposeId) || '';
    },
    store() {
      const { store } = this.$route.query;
      return (Array.isArray(store) ? store[0] : store) || '';
    },
    accountExposeUrl() {
      return this.$i18n.locale === 'de'
        ? `/de/konto/immobilien-suche/expose-${encodeURIComponent(
            this.exposeId,
          )}`
        : `/en/account/property-search/expose-${encodeURIComponent(
            this.exposeId,
          )}`;
    },
    loadingLogo() {
      return require('@/assets/images/property-radar/hf-logo-3d.svg');
    },
    // Heading for the store is a different destination, so it keeps its own
    // line; everything else reads the account's rotation.
    loaderMessages() {
      if (this.redirectingToStore) return [this.$t('opening-store')];
      const rotation = this.$t('loading-rotation');
      return Array.isArray(rotation) ? rotation : [this.$t('loading-title')];
    },
    loaderMessage() {
      return this.loaderMessages[this.messageIndex] || '';
    },
    qrCodeSrc() {
      return this.$i18n.locale === 'de'
        ? require('@/assets/images/property-radar/qr-app-de.svg')
        : require('@/assets/images/property-radar/qr-app-en.svg');
    },
    phonesSrc() {
      return this.$i18n.locale === 'de'
        ? require('@/assets/images/property-radar/download-phones-de.png')
        : require('@/assets/images/property-radar/download-phones-en.png');
    },
  },
  watch: {
    redirecting(active) {
      if (active) this.startMessageRotation();
      else this.stopMessageRotation();
    },
  },
  beforeDestroy() {
    this.stopMessageRotation();
    window.clearTimeout(this.handoffTimeoutId);
    this.cancelStorePaintWait();
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('pageshow', this.onPageShow);
    window.removeEventListener('focus', this.resetToPromo);
  },
  mounted() {
    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('pageshow', this.onPageShow);
    if (window.__hfRadarHandingOff) {
      window.__hfRadarHandingOff = false;
      this.redirecting = true;
      this.redirectingToStore = true;
      window.addEventListener('focus', this.resetToPromo, { once: true });
      // Backstop for a navigation that never happens, so the state can't stick.
      this.handoffTimeoutId = window.setTimeout(this.resetToPromo, 3000);
      return;
    }

    // ?store=1 is an explicit request for the app, so it wins over the property.
    if (this.store === '1' && !this.token && this.isMobileDevice()) {
      // Fallback for when the head script couldn't run.
      if (this.handedOffToStoreRecently()) {
        if (this.exposeId) this.openOnWeb();
        return;
      }
      this.redirecting = true;
      this.redirectingToStore = true;
      window.addEventListener('focus', this.resetToPromo, { once: true });
      this.storePaintCleanup = this.waitForStorePaint(() => {
        this.storePaintCleanup = null;
        if (!this.redirecting) return;
        this.recordStoreHandoff();
        window.location.replace(this.storeUrl());
      });
      // A redirect that never lands must not leave a spinner behind.
      this.handoffTimeoutId = window.setTimeout(this.resetToPromo, 3000);
      return;
    }

    // A property link the app didn't intercept. We can't tell from here whether
    // the app is installed, so never guess the store — show the property.
    if (this.exposeId) {
      this.redirecting = true;
      this.revealPage();
      this.openOnWeb();
      return;
    }

    if (this.token) {
      window.location.href = `hypofriendradar://login?token=${encodeURIComponent(
        this.token,
      )}`;
    }
  },
  methods: {
    recordStoreHandoff() {
      const now = Date.now();
      window.__hfRadarStoreHandoff = now;
      try {
        window.sessionStorage.setItem('hf-radar-store-handoff', String(now));
      } catch (error) {}
    },
    handedOffToStoreRecently() {
      let last = Number(window.__hfRadarStoreHandoff) || 0;
      try {
        last =
          last ||
          Number(window.sessionStorage.getItem('hf-radar-store-handoff'));
      } catch (error) {}
      const age = Date.now() - last;
      return !!last && age >= 0 && age < STORE_HANDOFF_GUARD_MS;
    },
    // When the tab comes back from the store (focus, visibility, or bfcache),
    // the redirect is over — drop the loader back to the promo so the user
    // never lands on a spinner that never resolves. iOS doesn't reliably fire
    // visibilitychange on the way back, hence the focus listener too.
    resetToPromo() {
      this.cancelStorePaintWait();
      window.clearTimeout(this.handoffTimeoutId);
      this.redirecting = false;
      this.redirectingToStore = false;
    },
    onVisibility() {
      if (this.redirecting && !document.hidden) this.resetToPromo();
    },
    onPageShow(event) {
      if (event.persisted && this.redirecting) this.resetToPromo();
    },
    isMobileDevice() {
      const ua = navigator.userAgent;
      return (
        /iphone|ipad|ipod|android/i.test(ua) ||
        (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1)
      );
    },
    storeUrl() {
      if (!/android/i.test(navigator.userAgent)) return APP_STORE_URL;
      const lang = this.$i18n.locale === 'de' ? 'de' : 'en';
      return `${PLAY_STORE_URL}&hl=${lang}`;
    },
    waitForStorePaint(leave) {
      let left = false;
      let hiddenTimeoutId = null;
      let backstopTimeoutId = null;
      let domContentLoadedHandler = null;

      const leaveIfHidden = () => {
        if (document.hidden) leaveOnce();
      };
      const cleanup = () => {
        document.removeEventListener('visibilitychange', leaveIfHidden);
        if (domContentLoadedHandler) {
          document.removeEventListener(
            'DOMContentLoaded',
            domContentLoadedHandler,
          );
        }
        window.clearTimeout(hiddenTimeoutId);
        window.clearTimeout(backstopTimeoutId);
      };
      const leaveOnce = () => {
        if (left) return;
        left = true;
        cleanup();
        leave();
      };

      if (window.requestAnimationFrame && !document.hidden) {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(leaveOnce);
        });
        document.addEventListener('visibilitychange', leaveIfHidden);
        hiddenTimeoutId = window.setTimeout(leaveIfHidden, 600);
        backstopTimeoutId = window.setTimeout(leaveOnce, 3000);
      } else if (document.readyState === 'loading') {
        domContentLoadedHandler = leaveOnce;
        document.addEventListener('DOMContentLoaded', domContentLoadedHandler, {
          once: true,
        });
      } else {
        leaveOnce();
      }

      return () => {
        left = true;
        cleanup();
      };
    },
    cancelStorePaintWait() {
      if (!this.storePaintCleanup) return;
      this.storePaintCleanup();
      this.storePaintCleanup = null;
    },
    goToStore() {
      this.recordStoreHandoff();
      window.location.href = this.storeUrl();
    },
    // The head hides #__nuxt, and the loader lives inside it.
    revealPage() {
      document.documentElement.classList.remove('hf-radar-awaiting-property');
    },
    // Same cadence as the account loader: 2.8s a message, half a second of
    // that spent fading the old one out.
    startMessageRotation() {
      this.stopMessageRotation();
      this.messageIndex = 0;
      this.loaderFadeClass = 'is-in';
      if (this.loaderMessages.length <= 1) return;
      this.rotateIntervalId = window.setInterval(() => {
        this.loaderFadeClass = 'is-out';
        this.fadeTimeoutId = window.setTimeout(() => {
          this.messageIndex =
            (this.messageIndex + 1) % this.loaderMessages.length;
          this.loaderFadeClass = '';
          this.$nextTick(() => {
            this.loaderFadeClass = 'is-in';
          });
        }, 500);
      }, 2800);
    },
    stopMessageRotation() {
      window.clearInterval(this.rotateIntervalId);
      window.clearTimeout(this.fadeTimeoutId);
      this.rotateIntervalId = null;
      this.fadeTimeoutId = null;
    },
    openOnWeb() {
      window.location.replace(this.accountExposeUrl);
    },
  },
};
